import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { CompBadge, CompCard } from '../components/CompCard';
import { AppLink, useGo } from '../components/Nav';
import { VideosTable } from '../components/VideosTable';
import {
  ExternalLink,
  Fact,
  Facts,
  Identity,
  LoadMore,
  Mono,
  Rows,
  SearchBox,
  Section,
  Segmented,
  SubscriptionBadge,
  Table,
  Td,
  Th,
  Tr,
} from '../components/data';
import { Badge, ErrorState, Loading, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/api';
import { stripeCustomerUrl, stripeSubscriptionUrl } from '../lib/env';
import { shortDate, timeAgo } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';
import {
  fetchOrg,
  fetchOrgs,
  grantOrgComp,
  qk,
  revokeOrgComp,
  type OrgDetail,
  type SubscriptionStatus,
} from '../queries/admin';

type StatusFilter = '' | SubscriptionStatus;

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trial' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'incomplete', label: 'No plan' },
];

function plan(interval: 'month' | 'year'): string {
  return interval === 'year' ? 'Yearly' : 'Monthly';
}

/** Where the team's plan stands once the override is taken into account. */
function PlanState({ org }: { org: OrgDetail }) {
  if (org.comp?.active) {
    return <>On the plan because we comped it{org.stripeSubscriptionId ? ', whatever the subscription does' : ''}.</>;
  }
  if (org.planLive) return <>On a live plan through Stripe. Comping would change nothing until that lapses.</>;
  return <>No live plan: paid seats on this team carry none of the benefits, and the team takes no new recordings.</>;
}

export function TeamsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const query = useDebounced(q.trim());
  const go = useGo();

  const orgs = useInfiniteQuery({
    queryKey: qk.orgs(query, status),
    queryFn: ({ pageParam }) => fetchOrgs(query, status, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = orgs.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="Teams" subtitle="Organizations and what they pay for." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchBox value={q} onChange={setQ} placeholder="Search by name or slug" busy={orgs.isFetching && !orgs.isFetchingNextPage} />
        <Segmented value={status} onChange={setStatus} options={STATUS_OPTIONS} />
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Team</Th>
            <Th>Plan</Th>
            <Th align="right">Seats</Th>
            <Th align="right">People</Th>
            <Th align="right">Recordings</Th>
            <Th>Next</Th>
            <Th align="right">Created</Th>
          </tr>
        </thead>
        <Rows query={orgs} columns={7} empty={query || status ? 'No teams match.' : 'No teams yet.'}>
          {rows.map((org) => (
            <Tr key={org.id} onClick={() => go('/teams/$id', { params: { id: org.id } })}>
              <Td className="max-w-[18rem]">
                <span className="block truncate text-chalk">{org.name}</span>
                <span className="block truncate text-caption text-chalk-faint">{org.slug}</span>
              </Td>
              <Td>
                <span className="flex flex-wrap items-center gap-2">
                  <SubscriptionBadge status={org.subscriptionStatus} />
                  <CompBadge comp={org.comp} />
                  <span className="text-caption text-chalk-dim">{plan(org.billingInterval)}</span>
                </span>
              </Td>
              <Td align="right">
                {org.seatsUsed.toLocaleString()} <span className="text-chalk-faint">/ {org.seatCount.toLocaleString()}</span>
              </Td>
              <Td align="right">
                {org.memberCount.toLocaleString()}
                {org.pendingInvites > 0 ? <span className="text-chalk-faint"> +{org.pendingInvites} invited</span> : null}
              </Td>
              <Td align="right">{org._count.videos.toLocaleString()}</Td>
              <Td className="whitespace-nowrap text-chalk-dim">
                {org.cancelAt
                  ? `Ends ${shortDate(org.cancelAt)}`
                  : org.subscriptionStatus === 'trialing' && org.trialEndsAt
                    ? `Trial ends ${shortDate(org.trialEndsAt)}`
                    : org.renewsAt
                      ? `Renews ${shortDate(org.renewsAt)}`
                      : ''}
              </Td>
              <Td align="right" className="whitespace-nowrap text-chalk-dim" title={org.createdAt}>
                {timeAgo(org.createdAt)}
              </Td>
            </Tr>
          ))}
        </Rows>
      </Table>
      <LoadMore query={orgs} />
    </>
  );
}

export function TeamPage({ id }: { id: string }) {
  const org = useQuery({ queryKey: qk.org(id), queryFn: () => fetchOrg(id) });

  if (org.isPending) {
    return (
      <>
        <PageHeader title="Team" />
        <Loading />
      </>
    );
  }
  if (org.isError) {
    return (
      <>
        <PageHeader title="Team" />
        <ErrorState message={errorMessage(org.error)} onRetry={() => void org.refetch()} />
      </>
    );
  }

  const data = org.data;
  const active = data.memberships.filter((membership) => membership.status === 'active');
  const seatsUsed = active.filter((membership) => membership.seat === 'paid').length + data.invites.filter((invite) => invite.seat === 'paid').length;

  return (
    <>
      <p className="mb-3 text-caption text-chalk-faint">
        <AppLink to="/teams" className="hover:text-chalk-dim">
          Teams
        </AppLink>{' '}
        /
      </p>
      <PageHeader
        title={data.name}
        subtitle={data.slug}
        actions={
          <>
            <SubscriptionBadge status={data.subscriptionStatus} />
            <CompBadge comp={data.comp} />
          </>
        }
      />

      <Facts>
        <Fact label="Plan">
          {plan(data.billingInterval)}, {data.seatCount.toLocaleString()} {data.seatCount === 1 ? 'seat' : 'seats'}
        </Fact>
        <Fact label="Seats used">
          {seatsUsed.toLocaleString()} <span className="text-chalk-faint">of {data.seatCount.toLocaleString()}</span>
        </Fact>
        <Fact label="Created">
          {shortDate(data.createdAt)} <span className="text-chalk-faint">({timeAgo(data.createdAt)})</span>
        </Fact>
        <Fact label="Trial ends">{data.trialEndsAt ? shortDate(data.trialEndsAt) : null}</Fact>
        <Fact label="Renews">{data.renewsAt ? shortDate(data.renewsAt) : null}</Fact>
        <Fact label="Cancels">{data.cancelAt ? shortDate(data.cancelAt) : null}</Fact>
        <Fact label="Stripe customer">
          {data.stripeCustomerId ? <ExternalLink href={stripeCustomerUrl(data.stripeCustomerId)}>{data.stripeCustomerId}</ExternalLink> : null}
        </Fact>
        <Fact label="Stripe subscription">
          {data.stripeSubscriptionId ? (
            <ExternalLink href={stripeSubscriptionUrl(data.stripeSubscriptionId)}>{data.stripeSubscriptionId}</ExternalLink>
          ) : null}
        </Fact>
        <Fact label="Team id">
          <Mono>{data.id}</Mono>
        </Fact>
      </Facts>

      <Section title="Plan override">
        <CompCard
          title="Comped plan"
          state={<PlanState org={data} />}
          blurb="While it runs, the team counts as being on a live plan: every paid seat on it gets the plan's benefits and the team keeps taking recordings. Stripe is not touched, so the status above still says whatever Stripe last said."
          comp={data.comp}
          grant={(body) => grantOrgComp(data.id, body)}
          revoke={() => revokeOrgComp(data.id)}
          invalidates={[qk.org(data.id), ['admin', 'orgs'], ['admin', 'users'], ['admin', 'user']]}
        />
      </Section>

      <Section title="People" count={data.memberships.length}>
        <Table>
          <thead>
            <tr>
              <Th>Person</Th>
              <Th>Role</Th>
              <Th>Seat</Th>
              <Th>Status</Th>
              <Th align="right">Joined</Th>
            </tr>
          </thead>
          <tbody>
            {data.memberships.map((membership) => (
              <Tr key={membership.id}>
                <Td className="max-w-[20rem]">
                  <AppLink to="/users/$id" params={{ id: membership.user.id }} className="block hover:underline">
                    <Identity name={membership.user.name} email={membership.user.email} avatarUrl={membership.user.avatarUrl} />
                  </AppLink>
                </Td>
                <Td className="capitalize">{membership.role}</Td>
                <Td>
                  <Badge tone={membership.seat === 'paid' ? 'info' : 'neutral'}>{membership.seat}</Badge>
                </Td>
                <Td>
                  {membership.status === 'active' ? <span className="text-chalk-dim">active</span> : <Badge tone="caution">suspended</Badge>}
                </Td>
                <Td align="right" className="whitespace-nowrap text-chalk-dim" title={membership.createdAt}>
                  {timeAgo(membership.createdAt)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Section>

      {data.invites.length > 0 ? (
        <Section title="Invitations out" count={data.invites.length}>
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Seat</Th>
                <Th>Expires</Th>
                <Th align="right">Sent</Th>
              </tr>
            </thead>
            <tbody>
              {data.invites.map((invite) => (
                <Tr key={invite.id}>
                  <Td>
                    <span className="text-chalk">{invite.email}</span>
                    {invite.name ? <span className="ml-2 text-caption text-chalk-faint">{invite.name}</span> : null}
                  </Td>
                  <Td className="capitalize">{invite.role}</Td>
                  <Td>
                    <Badge tone={invite.seat === 'paid' ? 'info' : 'neutral'}>{invite.seat}</Badge>
                  </Td>
                  <Td className="text-chalk-dim">{shortDate(invite.expiresAt)}</Td>
                  <Td align="right" className="whitespace-nowrap text-chalk-dim">
                    {timeAgo(invite.createdAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Section>
      ) : null}

      <Section title="Recent recordings" count={data._count.videos}>
        <VideosTable videos={data.videos} empty="Nothing recorded in this team yet." />
      </Section>
    </>
  );
}
